import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CHARACTER_PROFILES, {
  CHARACTER_PROFILES_BY_SLUG,
} from '../data/characterProfiles';

const TELEGRAM_LINK = 'https://t.me/hotbump_bot';

// Переводы
const TRANSLATIONS = {
  ru: {
    characterNotFound: "Персонаж не найден",
    characterNotFoundDesc: "Возможно, вы перешли по устаревшей ссылке или допустили опечатку в адресе страницы.",
    backToCatalog: "Вернуться к каталогу",
    back: "← Назад",
    whatMakesHerSpecial: "Чем она выделяется",
    defaultHighlight: "Индивидуальный стиль общения и сценарии, адаптированные под ваши запросы, помогают быстро погрузиться в нужное настроение.",
    whoIsThisFor: "Для кого этот персонаж",
    defaultAudience: "Идеально подойдёт, если вы хотите протестировать новый формат общения с ИИ и любите заранее понимать характер собеседника.",
    startChat: "Общаться",
    backToModels: "Вернуться к моделям",
    otherCharacters: "Другие персонажи"
  },
  en: {
    characterNotFound: "Character not found",
    characterNotFoundDesc: "You may have followed an outdated link or made a typo in the page address.",
    backToCatalog: "Back to catalog",
    back: "← Back",
    whatMakesHerSpecial: "What makes her special",
    defaultHighlight: "Individual communication style and scenarios adapted to your requests help you quickly get into the right mood.",
    whoIsThisFor: "Who is this character for",
    defaultAudience: "Perfect if you want to test a new AI communication format and like to understand your interlocutor's character in advance.",
    startChat: "Start chatting",
    backToModels: "Back to models",
    otherCharacters: "Other characters"
  }
};

const getProfileBySlug = (slugParam) => {
  if (!slugParam) {
    return null;
  }

  const normalized = slugParam.replace(/-/g, '_').toLowerCase();
  const profile = CHARACTER_PROFILES_BY_SLUG[normalized];
  if (profile) {
    return profile;
  }

  // fallback: сопоставляем с тем же slug, но без учёта регистра
  const fallback = CHARACTER_PROFILES.find(
    (item) => item.slug.toLowerCase() === normalized
  );

  return fallback || null;
};

function CharacterProfile({ language = 'ru', onLanguageChange }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const profile = useMemo(() => getProfileBySlug(slug), [slug]);

  // Функция для получения перевода
  const t = (key) => TRANSLATIONS[language]?.[key] || TRANSLATIONS.ru[key];

  const assetBaseUrl =
    process.env.NODE_ENV === 'production'
      ? 'https://hotbump.online/api'
      : 'http://localhost:8010';

  if (!profile) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col items-center justify-center text-gray-200 p-6">
        <div className="max-w-lg text-center bg-dark-800 border border-burgundy-800 rounded-xl p-8 shadow-2xl">
          <h1 className="text-3xl font-bold text-burgundy-300 mb-4">
            {t('characterNotFound')}
          </h1>
          <p className="text-gray-400 mb-6">
            {t('characterNotFoundDesc')}
          </p>
          <Link
            to="/characters"
            className="inline-flex items-center justify-center px-6 py-3 bg-burgundy-700 hover:bg-burgundy-600 rounded-lg font-semibold transition-colors"
          >
            {t('backToCatalog')}
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = `${assetBaseUrl}${profile.image}`;

  return (
    <div className="min-h-screen bg-dark-900 text-gray-100">
      <div className="max-w-5xl mx-auto px-4 py-10 md:py-16">
        <div className="flex justify-between items-center mb-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-sm text-gray-400 hover:text-burgundy-300 transition-colors"
          >
            {t('back')}
          </button>
          {/* Переключатель языка */}
          {onLanguageChange && (
            <div className="flex space-x-2">
              <button
                onClick={() => onLanguageChange('ru')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'ru' 
                    ? 'bg-burgundy-700 text-white' 
                    : 'bg-dark-700 text-gray-400 hover:text-white'
                }`}
              >
                RU
              </button>
              <button
                onClick={() => onLanguageChange('en')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'en' 
                    ? 'bg-burgundy-700 text-white' 
                    : 'bg-dark-700 text-gray-400 hover:text-white'
                }`}
              >
                EN
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="relative bg-dark-800 border border-burgundy-900 rounded-2xl overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
            <img
              src={imageUrl}
              alt={profile.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-burgundy-700/90 text-sm font-semibold tracking-wide uppercase">
                {(() => {
                  // Отладка: проверяем структуру данных
                  if (typeof profile.archetype === 'object' && profile.archetype !== null) {
                    return profile.archetype[language] || profile.archetype.ru || profile.archetype.en || '';
                  }
                  // Fallback для старого формата (строка)
                  return profile.archetype || '';
                })()}
              </span>
            </div>
          </div>

          <div className="flex flex-col space-y-6">
            <div>
              <h1 className="text-4xl font-extrabold text-white mb-4">
                {profile.name}
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed">
                {(() => {
                  // Отладка: проверяем структуру данных
                  if (typeof profile.description === 'object' && profile.description !== null) {
                    return profile.description[language] || profile.description.ru || profile.description.en || '';
                  }
                  // Fallback для старого формата (строка)
                  return profile.description || '';
                })()}
              </p>
            </div>

            <div className="bg-dark-800 border border-burgundy-900 rounded-xl p-6 space-y-4 shadow-xl">
              <h2 className="text-xl font-semibold text-burgundy-200">
                {t('whatMakesHerSpecial')}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {(() => {
                  if (profile.highlight) {
                    if (typeof profile.highlight === 'object' && profile.highlight !== null) {
                      return profile.highlight[language] || profile.highlight.ru || profile.highlight.en || '';
                    }
                    return profile.highlight;
                  }
                  return t('defaultHighlight');
                })()}
              </p>
            </div>

            <div className="bg-dark-800 border border-burgundy-900 rounded-xl p-6 shadow-xl">
              <h2 className="text-xl font-semibold text-burgundy-200 mb-3">
                {t('whoIsThisFor')}
              </h2>
              <p className="text-gray-300 leading-relaxed">
                {(() => {
                  if (profile.audience) {
                    if (typeof profile.audience === 'object' && profile.audience !== null) {
                      return profile.audience[language] || profile.audience.ru || profile.audience.en || '';
                    }
                    return profile.audience;
                  }
                  return t('defaultAudience');
                })()}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0 pt-2">
              <a
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-burgundy-700 hover:bg-burgundy-600 text-white font-bold rounded-lg shadow-lg hover:shadow-burgundy-600/40 transition-all duration-200"
              >
                {t('startChat')}
              </a>
              <Link
                to="/characters"
                className="inline-flex items-center justify-center px-6 py-3 border border-burgundy-700 text-burgundy-200 hover:bg-burgundy-700/10 font-semibold rounded-lg transition-colors duration-200"
              >
                {t('backToModels')}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-dark-800 border border-burgundy-900 rounded-2xl shadow-2xl p-6 md:p-8">
          <h2 className="text-2xl font-bold text-white mb-6">
            {t('otherCharacters')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {CHARACTER_PROFILES.filter(
              (item) => item.slug !== profile.slug
            ).map((item) => (
              <Link
                key={item.slug}
                to={`/characters/${item.slug}`}
                className="group block bg-dark-900 border border-burgundy-900 rounded-xl p-4 hover:border-burgundy-600 transition-all duration-200"
              >
                <div className="text-sm text-burgundy-300 font-semibold mb-2">
                  {(() => {
                    if (typeof item.archetype === 'object' && item.archetype !== null) {
                      return item.archetype[language] || item.archetype.ru || item.archetype.en || '';
                    }
                    return item.archetype || '';
                  })()}
                </div>
                <div className="text-base text-gray-200 font-bold group-hover:text-white transition-colors">
                  {item.name}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CharacterProfile;

